// src/pages/Information/unlistedAssignees.ts
/**
 * "Assigned but not on record" people.
 *
 * Some leaders and facilitators are doing real work, they are on a case or on a registration, but they
 * have no user in Identity and so never appear on `/Information/CareSupportLeaders` or
 * `/Information/facilitators`. Neither page filters anything client-side: `leaderService.list()` and
 * `facilitatorService.list()` simply do not return them, so the only way to surface them is to read the
 * **assignments** and subtract the people who are already on the list.
 *
 * These hooks return everyone an assignment references. Subtracting the known set is left to the caller,
 * deliberately: passing a `Set` into a hook makes it a dependency that changes identity on every render,
 * and the caller already holds the streamed list it needs to subtract.
 *
 * ⚠️ On both sides the identifier is the **userId on the assignment**. It may point at an Identity user
 * that was never created, which is the whole problem, so treat it as an opaque key rather than proof an
 * account exists.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useACCourseService,
  useCaseStreaming,
  useFacilitatorService,
} from '@actschurch/ccms-sdk';

/** Someone referenced by a case or registration assignment, with whatever the member cache knows. */
export interface AssignedPerson {
  /** User ID carried on the assignment. The join key, and possibly a dangling Identity reference. */
  userId: string;
  /** Best available name from the member cache. Null when the cache has nothing either. */
  name: string | null;
  /** Avatar from the member cache. */
  avatar: string | null;
  /** Leader type / facilitator status name, when the source carries one. */
  typeName: string | null;
  /** Matching hex colour, when the source carries one. */
  typeColor: string | null;
  /** How many assignments reference this person. Drives the "on N cases" hint. */
  assignmentCount: number;
}

/** Folds one sighting of a person into the accumulator, keeping the first non-empty value per field. */
function mergeSighting(
  into: Map<string, AssignedPerson>,
  userId: string | null | undefined,
  fields: Partial<Omit<AssignedPerson, 'userId' | 'assignmentCount'>>,
  countsAsAssignment = true,
) {
  const key = userId?.trim();
  if (!key) return;

  const existing = into.get(key) ?? {
    userId: key,
    name: null,
    avatar: null,
    typeName: null,
    typeColor: null,
    assignmentCount: 0,
  };

  if (countsAsAssignment) existing.assignmentCount += 1;
  if (!existing.name && fields.name?.trim()) existing.name = fields.name.trim();
  if (!existing.avatar && fields.avatar) existing.avatar = fields.avatar;
  if (!existing.typeName && fields.typeName?.trim()) existing.typeName = fields.typeName.trim();
  if (!existing.typeColor && fields.typeColor) existing.typeColor = fields.typeColor;

  into.set(key, existing);
}

const byAssignmentCountDesc = (a: AssignedPerson, b: AssignedPerson) =>
  b.assignmentCount - a.assignmentCount;

/**
 * Everyone who owns a case, drawn from the case list stream.
 *
 * `CareCaseListView` carries `userId`, `leaderName` and `leaderAvatar`, so one stream gives both the key
 * and something human-readable to show. It is the **owning** leader only: co-assigned leaders live in
 * `CareCaseModelView.leaders[]`, which would mean fetching every case individually, so a leader who is
 * only ever a second leader on someone else's case will not be found this way.
 */
export function useAssignedLeaders() {
  const { items: cases, isLoading, load } = useCaseStreaming(undefined, 50);

  useEffect(() => { load(); }, [load]);

  const people = useMemo(() => {
    const byUser = new Map<string, AssignedPerson>();
    cases.forEach((c) => {
      mergeSighting(byUser, c.userId, { name: c.leaderName, avatar: c.leaderAvatar });
    });
    return [...byUser.values()].sort(byAssignmentCountDesc);
  }, [cases]);

  return { people, isLoading, reload: load };
}

/**
 * Everyone referenced as a facilitator, from the two sources that actually carry a userId.
 *
 * ⚠️ `RegistrationListView` carries `leadFacilitatorName` but **no facilitator userId**, so the
 * registration list, the obvious source, cannot be used: there is no key to dedupe on or link to.
 * `registrationService.getFacilitators(id)` has the ids but is per registration, an N+1 across the whole
 * table. So this falls back to:
 *
 * 1. `facilitatorService.loadStats()`, one call, whose `activeCount` is registration-scoped. If the
 *    backend computes it by joining the Facilitator table it will never contain anyone unlisted, in
 *    which case this source is simply inert rather than wrong.
 * 2. Course-level assignments, `getFacilitators()` per course. Courses are a small set. This is the
 *    "approved to run this course" relation rather than "assigned to this registration", so it is a
 *    near neighbour of what we want, not an exact match.
 *
 * Both are wrapped so a 404 on an older backend degrades to "found nobody" instead of failing the page.
 * The exact fix is a facilitator userId on `RegistrationListView`, specced in `_deploy/_newEndPoints/`.
 */
export function useAssignedFacilitators() {
  const Facilitators = useFacilitatorService();
  const Courses = useACCourseService();

  const [people, setPeople] = useState<AssignedPerson[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    const byUser = new Map<string, AssignedPerson>();

    const [statsResult, coursesResult] = await Promise.allSettled([
      Facilitators.loadStats(),
      Courses.getAllViews(),
    ]);

    if (statsResult.status === 'fulfilled') {
      (statsResult.value ?? []).forEach((row) => {
        mergeSighting(
          byUser,
          row.userId,
          { name: row.fullName, avatar: row.avatar, typeName: row.statusName, typeColor: row.statusColor },
          false,
        );
        // activeCount is the real registration count, so take it rather than counting sightings.
        const entry = byUser.get(row.userId?.trim() ?? '');
        if (entry) entry.assignmentCount = Math.max(entry.assignmentCount, row.activeCount ?? 0);
      });
    } else if (import.meta.env.DEV) {
      console.warn('[unlistedAssignees] loadStats unavailable:', statsResult.reason);
    }

    if (coursesResult.status === 'fulfilled') {
      const courses = coursesResult.value ?? [];
      const perCourse = await Promise.allSettled(
        courses.map((course) => Courses.getFacilitators(course.courseID)),
      );
      perCourse.forEach((result) => {
        if (result.status !== 'fulfilled') return;
        (result.value ?? []).forEach((f) => {
          mergeSighting(byUser, f.userId, {
            name: f.fullName || [f.firstName, f.surname].filter(Boolean).join(' '),
            avatar: f.avatar,
            typeName: f.statusName,
            typeColor: f.statusColor,
          });
        });
      });
    } else if (import.meta.env.DEV) {
      console.warn('[unlistedAssignees] course list unavailable:', coursesResult.reason);
    }

    setPeople([...byUser.values()].sort(byAssignmentCountDesc));
    setIsLoading(false);
  }, [Facilitators, Courses]);

  useEffect(() => { load(); }, [load]);

  return { people, isLoading, reload: load };
}

/** Keeps only the people who are not already on the list the page streamed. */
export function subtractListed(people: AssignedPerson[], listedUserIds: Set<string>) {
  return people.filter((p) => !listedUserIds.has(p.userId));
}

/** Splits a cached full name into the first/last pair the ContactCard and table columns expect. */
export function splitName(name: string | null): { firstName: string; lastName: string } {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}
